import { useMemo } from 'react';

import { IKnmFormSubject } from 'KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';

type IPmErknmSubjectBaseFieldPaths = {
  [K in keyof IKnmFormSubject]-?: string;
};

interface IPmErknmSubjectFieldPathsExtended {
  isNkoFieldName: string;
  locationRegionName: string;
}

export interface IPmErknmSubjectFieldPaths extends IPmErknmSubjectBaseFieldPaths, IPmErknmSubjectFieldPathsExtended {}

type IBaseKeys = keyof IPmErknmSubjectBaseFieldPaths;
type IExtendedKeys = keyof IPmErknmSubjectFieldPathsExtended;

type IDuplicateKeys = IBaseKeys & IExtendedKeys;

// проверка на дублирование полей из IBaseKeys в IExtendedKeys, дублирующиеся поля будут в IDuplicateKeys
const isAssertNoDuplicates: IDuplicateKeys extends never ? true : false = true;

// eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
isAssertNoDuplicates;

// объект для генерации списка полей в рантайме
const FIELD_KEYS: Record<keyof IPmErknmSubjectFieldPaths, true> = {
  // Base fields (from IKnmFormSubject)
  inn: true,
  position: true,
  ogrn: true,
  okveds: true,
  organizationName: true,
  firstOrganizationName: true,
  mspCode: true,
  mspRegistryDate: true,
  checkSubjectResult: true,
  dateCreate: true,
  isFiz: true,
  nkoForeignAgentsRegistryNumber: true,
  nkoForeignAgentsRegistryInclusionDate: true,
  nkoForeignAgentsRegistryExclusionDate: true,
  isNko: true,
  kpp: true,
  smevRequestStatus: true,
  rafpNza: true,
  country: true,
  isSonko: true,
  locationAddress: true,
  workAddress: true,
  factualAddress: true,
  snils: true,
  document: true,
  ervkId: true,
  isPresentInItRegistry: true,
  itAccreditationResponse: true,
  smevItAccreditationRequestStatus: true,
  inspectionResults: true,
  isFilledFromLink: true,
  locationregion: true,
  locationnotInFIAS: true,
  locationmanualAddress: true,
  locationfiasRegion: true,
  locationisErrorFiasValue: true,
  locationRegionCodeFromFns: true,
  isOrganizationNew: true,
  subjectTypePm: true,
  subjectTypeKnm: true,
  organizationGuid: true,
  organizationAutocomplete: true,
  isAutocomplete: true,

  // Extended fields
  isNkoFieldName: true,
  locationRegionName: true,
};

interface IUsePmErknmSubjectFieldsProps {
  name: string | undefined;
  isRemovePrefix?: boolean;
}

/**
 * хук, который генерирует согласованный набор путей к полям формы.
 *
 * @example
 * const subjectFieldNames = usePmErknmSubjectFields({ name: 'legalEntity' });
 * <SelectInputField id={subjectFieldNames.subjectType} name={subjectFieldNames.subjectType}/>
 *
 * @param {IUsePmErknmSubjectFieldsProps} props .
 * @returns {PmErknmSubjectFieldPaths} Объект, сопоставляющий логические имена полей с их полными путями в форме.
 */
export const usePmErknmSubjectFields = ({
  name,
  isRemovePrefix,
}: IUsePmErknmSubjectFieldsProps): IPmErknmSubjectFieldPaths => {
  return useMemo(() => {
    const prefix = name ?? '';

    const rawFields = {} as IPmErknmSubjectFieldPaths;

    (Object.keys(FIELD_KEYS) as Array<keyof IPmErknmSubjectFieldPaths>).forEach(key => {
      rawFields[key] = isRemovePrefix ? `${key}` : `${prefix}.${key}`;
    });

    return rawFields;
  }, [name]);
};
